import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { FileDown, Check, ArrowRight, ShieldCheck } from 'lucide-react';
import { PageSEO } from '../components/seo/SchemaMarkup';
import { API } from '../lib/api';

const INCLUDES = [
  '12 popular peptides with research dose & frequency',
  'BPC-157, TB-500, Retatrutide, Tesamorelin & more',
  'Storage rules — powder, reconstituted & BAC water',
  'Reconstitution basics done right (no guesswork)',
  'Printable one-page quick reference',
];

export default function PeptideCheatSheetPage() {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [status, setStatus] = useState('idle'); // idle | loading | done | error
  const [err, setErr] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    if (!email.includes('@')) { setErr('Please enter a valid email.'); return; }
    setStatus('loading'); setErr('');
    try {
      const res = await fetch(`${API}/lead-magnet/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim(), first_name: name.trim(), magnet: 'peptide-cheat-sheet' }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Something went wrong');
      // Trigger the download
      const a = document.createElement('a');
      a.href = `${API}${data.download_url}`;
      a.download = 'NinjAthlete-Peptide-Cheat-Sheet-2026.pdf';
      document.body.appendChild(a); a.click(); a.remove();
      setStatus('done');
    } catch (e2) {
      setErr(e2.message); setStatus('error');
    }
  };

  return (
    <main className="pt-20 min-h-screen bg-[#0a0a0a]" data-testid="peptide-cheat-sheet-page">
      <PageSEO
        title="Peptide Cheat Sheet 2026 (Free PDF): Dosing, Storage & Reconstitution | NinjAthlete"
        description="Download the free 2026 Peptide Cheat Sheet PDF — a one-page dosing quick-reference for BPC-157, TB-500, Retatrutide, Tesamorelin and 8 more, plus storage & reconstitution rules."
        path="/peptide-cheat-sheet"
        breadcrumbs={[
          { name: 'Home', url: '/' },
          { name: 'Peptides', url: '/peptides' },
          { name: 'Peptide Cheat Sheet 2026', url: '/peptide-cheat-sheet' },
        ]}
      />

      <div className="max-w-5xl mx-auto px-6 py-10 grid md:grid-cols-2 gap-12 items-start">
        {/* Left: pitch */}
        <div>
          <p className="font-mono text-[10px] tracking-[0.25em] text-[#C7FA3E] uppercase mb-4">Free Download · PDF</p>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl leading-[0.95] text-white mb-5">
            THE 2026 <span className="text-[#C7FA3E]">PEPTIDE<br />CHEAT SHEET</span>
          </h1>
          <p className="text-zinc-400 text-base leading-relaxed mb-6">
            Every popular peptide's research dose, frequency, storage and reconstitution rules — on one
            printable page. The reference our community keeps next to their vials.
          </p>
          <ul className="space-y-3 mb-8" data-testid="cheat-sheet-includes">
            {INCLUDES.map((i) => (
              <li key={i} className="flex items-start gap-3 text-sm text-zinc-300">
                <Check className="w-4 h-4 text-[#C7FA3E] mt-0.5 shrink-0" /> {i}
              </li>
            ))}
          </ul>
          <p className="text-zinc-600 text-xs flex items-center gap-2">
            <ShieldCheck className="w-3.5 h-3.5" /> Educational reference only — not medical advice.
          </p>
        </div>

        {/* Right: gate form / success */}
        <div className="bg-[#141414] border border-zinc-800 p-8 md:sticky md:top-28">
          {status === 'done' ? (
            <div className="text-center py-6" data-testid="cheat-sheet-success">
              <div className="w-14 h-14 rounded-full bg-[#C7FA3E]/15 flex items-center justify-center mx-auto mb-4">
                <FileDown className="w-7 h-7 text-[#C7FA3E]" />
              </div>
              <h2 className="font-heading text-2xl text-white mb-2">CHECK YOUR DOWNLOADS</h2>
              <p className="text-zinc-400 text-sm mb-6">Your Peptide Cheat Sheet is downloading. Didn't get it?</p>
              <a href={`${API}/lead-magnet/peptide-cheat-sheet.pdf`} target="_blank" rel="noopener noreferrer"
                 className="inline-flex items-center gap-2 text-[#C7FA3E] text-sm hover:text-white transition-colors" data-testid="cheat-sheet-manual-download">
                <FileDown className="w-4 h-4" /> Download again
              </a>
              <div className="mt-8 pt-6 border-t border-zinc-800">
                <Link to="/peptides" className="inline-flex items-center gap-2 text-white text-sm hover:text-[#C7FA3E] transition-colors" data-testid="cheat-sheet-to-hub">
                  Explore full peptide protocols <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          ) : (
            <form onSubmit={submit} data-testid="cheat-sheet-form">
              <h2 className="font-heading text-2xl text-white mb-1">GET THE FREE PDF</h2>
              <p className="text-zinc-500 text-sm mb-6">Enter your email and it's yours instantly.</p>
              <input
                type="text" placeholder="First name (optional)" value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full bg-[#0a0a0a] border border-zinc-800 focus:border-[#C7FA3E] text-white px-4 py-3 mb-3 outline-none transition-colors"
                data-testid="cheat-sheet-name-input"
              />
              <input
                type="email" placeholder="you@email.com" value={email} required
                onChange={(e) => setEmail(e.target.value)}
                className="w-full bg-[#0a0a0a] border border-zinc-800 focus:border-[#C7FA3E] text-white px-4 py-3 mb-4 outline-none transition-colors"
                data-testid="cheat-sheet-email-input"
              />
              {err && <p className="text-red-400 text-xs mb-3" data-testid="cheat-sheet-error">{err}</p>}
              <button type="submit" disabled={status === 'loading'}
                className="w-full bg-[#C7FA3E] text-black font-heading text-lg tracking-wider py-4 hover:bg-white transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
                data-testid="cheat-sheet-submit">
                <FileDown className="w-5 h-5" />
                {status === 'loading' ? 'SENDING…' : 'DOWNLOAD CHEAT SHEET'}
              </button>
              <p className="text-zinc-600 text-[11px] text-center mt-3">No spam. Unsubscribe anytime.</p>
            </form>
          )}
        </div>
      </div>

      {/* SEO body content */}
      <div className="max-w-3xl mx-auto px-6 pb-20 prose-invert">
        <h2 className="font-heading text-2xl text-white mb-3">What's in the peptide cheat sheet?</h2>
        <p className="text-zinc-400 text-sm leading-relaxed mb-4">
          The NinjAthlete Peptide Cheat Sheet 2026 is a free, printable one-page dosing chart covering the
          most-used research peptides — including BPC-157, TB-500, GHK-Cu, KPV, Retatrutide, Semaglutide,
          Tirzepatide, Tesamorelin, AOD-9604, CJC-1295/Ipamorelin, MK-677 and MOTS-c. For each, you get the
          typical research dose, dosing frequency, and primary use, plus essential storage and reconstitution
          rules so you handle your vials correctly.
        </p>
        <p className="text-zinc-400 text-sm leading-relaxed">
          Want the full protocols behind the numbers? Explore the{' '}
          <Link to="/peptides" className="text-[#C7FA3E] underline">Peptide Hub</Link>, calculate exact doses with the{' '}
          <Link to="/tools/peptide-dosing" className="text-[#C7FA3E] underline">Peptide Dosing Calculator</Link>, or learn where to source safely in{' '}
          <Link to="/blogs/article/peptides-near-me" className="text-[#C7FA3E] underline">where to buy peptides near you</Link>.
        </p>
      </div>
    </main>
  );
}
